import { Dive } from '../types/Dive';
import { DEMO_DATA } from '../data/demoData';

export class DemoService {
  private static readonly DEMO_TOKEN = 'DEMO_MODE_TOKEN';

  /**
   * Check if the app is in demo mode
   */
  static isDemoMode(accessToken: string | null): boolean {
    return accessToken === this.DEMO_TOKEN;
  }

  /**
   * Enable demo mode
   */
  static enableDemoMode(): string {
    return this.DEMO_TOKEN;
  }

  /**
   * Get demo dives data
   */
  static async getDemoDives(limit: number = 1000): Promise<Dive[]> {
    const dives = DEMO_DATA.dives;
    return dives.slice(0, limit);
  }

  /**
   * Get demo dive by ID
   */
  static async getDemoDive(diveId: string): Promise<Dive> {
    const dive = DEMO_DATA.dives.find(d => d.id === diveId);
    if (!dive) {
      throw new Error(`Demo dive with ID ${diveId} not found`);
    }

    return dive;
  }

  /**
   * Demo geocoding - returns the existing location or a simulated update
   */
  static async geocodeDemoDiveLocation(diveId: string): Promise<{ diveId: string; location: string; coordinates: string }> {
    const dive = DEMO_DATA.dives.find(d => d.id === diveId);
    if (!dive) {
      throw new Error(`Demo dive with ID ${diveId} not found`);
    }

    // Simulate geocoding delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // If location already looks geocoded, return it as-is
    if (dive.location && !dive.location.includes('°')) {
      return {
        diveId,
        location: dive.location,
        coordinates: dive.latitude && dive.longitude ? `${dive.latitude}, ${dive.longitude}` : ''
      };
    }

    // Otherwise, simulate a geocoded location
    const demoLocations = [
      'Red Sea, Egypt',
      'Great Barrier Reef, Australia', 
      'Maldives, Indian Ocean',
      'Cenote Dos Ojos, Mexico',
      'Blue Hole, Belize',
      'Sipadan Island, Malaysia',
      'Poor Knights Islands, New Zealand',
      'Channel Islands, California'
    ];

    const randomLocation = demoLocations[Math.floor(Math.random() * demoLocations.length)];

    return {
      diveId,
      location: randomLocation,
      coordinates: dive.latitude && dive.longitude ? `${dive.latitude}, ${dive.longitude}` : ''
    };
  }

  /**
   * Check if demo data is available
   */
  static hasDemoData(): boolean {
    return true; // Built-in demo data is always available
  }

  /**
   * Get demo data info
   */
  static getDemoDataInfo(): { exportDate: string; divesCount: number } | null {
    return {
      exportDate: DEMO_DATA.exportDate,
      divesCount: DEMO_DATA.dives.length
    };
  }
}